import ArrowUpIcon from "@heroicons/react/24/solid/ArrowUpIcon";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import { useMemo, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { useFormik } from "formik";
import * as Yup from "yup";
import { validateCSV } from "@/pages/manage-offers/UploadOfferForm/utils";

import {
  baseStyle,
  focusedStyle,
  acceptStyle,
  rejectStyle,
} from "@/pages/manage-offers/UploadOfferForm/styles";

import useSnackbar from "@/hooks/use-snackbar";
import { useNavigate } from "react-router-dom";
import { useDeleteOfferDataMutation } from "@/app/features/offers/offersApiSlice";

const batchSize = 1000;
const validationSchema = Yup.object().shape({
  file: Yup.mixed().required("File is required"),
});

const UploadOfferForm = ({ socket }) => {
  const { showSnackbar, snackbar } = useSnackbar();
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const [validData, setValidData] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [totalRowsProcessed, setTotalRowsProcessed] = useState(0);
  const navigate = useNavigate();

  const [deleteOfferData, { isLoading: deleteOfferDataLoading, isError }] =
    useDeleteOfferDataMutation();

  useEffect(() => {
    // Listen for 'progress' events from the server
    const handleProgressUpdate = async (update) => {
      // Handle error or completion messages received from the backend
      if (update.error) {
        // Display error message to the user
        showSnackbar({
          type: "error",
          message: update.error,
        });
        setLoading(false);
        // setProgress(0);
        setTotalRowsProcessed(0);
      }

      if (update.complete) {
        const processedRows = totalRowsProcessed + update.totalRows;
        setTotalRowsProcessed(processedRows);
        const progress = Math.floor((processedRows / validData.length) * 100);
        setProgress(progress);

        if (processedRows === validData.length) {
          showSnackbar({
            message: update.message,
          });
          setLoading(false);

          setTimeout(() => {
            navigate("/offers");
          }, 2000);
        } else {
          // if loading is continue
          if (isLoading) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            const batchRows = validData.slice(
              processedRows,
              processedRows + batchSize
            );

            socket.emit("offer-upload", {
              rows: batchRows,
              rowIndex: processedRows + batchRows.length,
            });
          }
        }
      }
    };

    socket.on("progress", handleProgressUpdate);

    // Clean up the event listener when the component unmounts
    return () => {
      socket.off("progress", handleProgressUpdate);
    };
  }, [
    totalRowsProcessed,
    showSnackbar,
    socket,
    validData,
    navigate,
    isLoading,
  ]);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      maxFiles: 1,
      accept: { "text/csv": [] },
      onDrop: async (acceptedFiles) => {
        formik.setFieldValue("file", acceptedFiles[0]);
        setAcceptedFiles(acceptedFiles);
        formik.setErrors({});

        try {
          const { validDataCount, invalidDataCount, validData } =
            await validateCSV(acceptedFiles[0]);

          console.log(validData);

          setValidData(validData);

          const message = `${validDataCount} will be inserted and ${invalidDataCount} will not be inserted`;
          formik.setFieldValue("message", message);
        } catch (error) {
          console.log(error);
          showSnackbar({
            type: "error",
            message: error?.message,
          });
          formik.setErrors({ submit: error.message });
        }
      },
    });

  const formik = useFormik({
    initialValues: {
      file: null,
      submit: null,
      message: "",
      isFileValid: false,
    },
    onSubmit: async (values, helpers) => {
      try {
        await deleteOfferData();
      } catch (error) {
        console.log(error);
      }

      try {
        setLoading(true);
        setProgress(0);
        setTotalRowsProcessed(0);
        const batchRows = validData.slice(totalRowsProcessed, batchSize);
        socket.emit("offer-upload", {
          // rows: validData,
          rows: batchRows,
          rowIndex: batchRows.length,
        });
      } catch (err) {
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    },
    validationSchema: validationSchema,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const acceptedFileItems = acceptedFiles.map((file) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ));

  return (
    <Box
      component="main"
      sx={{
        alignItems: "center",
        display: "flex",
        flexGrow: 1,
        minHeight: "100%",
      }}
    >
      {snackbar}
      <Container maxWidth="sm">
        <section className="container">
          <Typography variant="h4" mb={4}>
            Add File
          </Typography>
          <form onSubmit={formik.handleSubmit}>
            {!formik.values.file ? (
              <>
                <div {...getRootProps({ style })}>
                  <input {...getInputProps()} name="file" label="file" />
                  <p>
                    Drag &apos;n&apos; drop some files here, or click to select
                    files
                  </p>
                  <p>Only CSV files allowed</p>
                  <em>(You can upload a maximum of 1 file)</em>
                </div>
              </>
            ) : null}
            <aside>
              {acceptedFileItems.length > 0 && <h4>Selected Files</h4>}
              <ul>{acceptedFileItems}</ul>
            </aside>

            {formik.errors.file && (
              <Typography color="error" sx={{ mt: 3 }} variant="body2">
                {formik.errors.file}
              </Typography>
            )}

            {formik.errors.submit && (
              <Typography color="error" sx={{ mt: 3 }} variant="body2">
                {formik.errors.submit}
              </Typography>
            )}

            {!formik.values.file ? (
              <Button
                sx={{ mt: 3 }}
                fullWidth
                variant="contained"
                disabled={!formik.values.file}
              >
                Check File
              </Button>
            ) : null}

            {formik.values.file ? (
              <Typography mt="3rem" variant="subtitle1">
                {formik.values.message}
              </Typography>
            ) : null}

            {formik.values.file ? (
              <Stack direction="row" spacing={3} sx={{ mt: 3 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    formik.resetForm();
                    setAcceptedFiles([]);
                    setValidData([]);
                    setLoading(false);
                    setProgress(0);
                    setTotalRowsProcessed(0);
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress /> : "Reset"}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress /> : "Continue"}
                </Button>
              </Stack>
            ) : null}

            {formik.values.isFileValid ? (
              <Button
                startIcon={
                  <SvgIcon fontSize="small">
                    <ArrowUpIcon />
                  </SvgIcon>
                }
                sx={{ mt: 3 }}
                fullWidth
                variant="contained"
                type="submit"
              >
                Upload File
              </Button>
            ) : null}
          </form>
          {progress > 0 && <div>Progress: {progress}%</div>}
        </section>
      </Container>
    </Box>
  );
};

export default UploadOfferForm;
